import { useEffect, useState } from 'react';

import { native } from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { useSessionControl } from './sessionControlContext';
import { RealtimePitchGraph } from '../../../components';

/**
 * 실시간 음정 그래프 패널 — 공용 `RealtimePitchGraph`(세션 전체 기록 보관, 가로 ~8초
 * 시간축 뷰포트 + 좌우 드래그 스크럽·LIVE 복귀, voiced만 연결·confidence<0.5 끊김)를
 * 재사용하고, 옥타브 글리치 median(3) 스무딩 토글을 얹는다.
 * 스무딩 상태는 패널이 소유하고 `smooth` prop으로 그래프에 전달한다. 기본 on —
 * 엔진 post-processor가 남기는 잔여 옥타브 점프(fry 구간, 테이크당 1~2개)를 한 겹 더
 * 누른다. 원본이 보고 싶을 때 끄는 토글이다.
 */
export const RealtimePitchGraphPanel = () => {
  const [smooth, setSmooth] = useState(true);
  const { isStopped, isNative } = useSessionControl();

  // 정지해도 마지막 세션 곡선을 남겨두기 위해 "데이터가 있었는지" 추적한다.
  // 그래프(RealtimePitchGraph)는 session_started에서만 버퍼를 비우고 정지 시엔
  // 마지막 곡선을 그대로 유지하므로, 데이터가 있으면 빈 상태 오버레이로 덮지 않는다.
  const [hasData, setHasData] = useState(false);
  useEffect(
    () =>
      native.on('pitchTrackerEvent', (event) => {
        if (event.type === 'session_started') {
          setHasData(false); // 새 세션 시작 → 그래프도 초기화됨
        } else if (event.type === 'pitch_frame') {
          setHasData(true);
        }
      }),
    [],
  );

  // 빈 안내는 "정지 + 보여줄 데이터 없음"일 때만. 정지했지만 이전 곡선이 있으면
  // 곡선을 남기고 작은 '정지됨' 뱃지만 표시한다.
  const showEmpty = isStopped && !hasData;
  const showFrozenBadge = isStopped && hasData;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">
            좌우 드래그로 과거 보기 · LIVE로 복귀
          </span>
          <InfoTip label="음정 그래프 축 설명">
            세로축은 음높이로, 목소리 범위에 맞춰 자동으로 확대·축소됩니다(반음
            격자 + C 라벨). 가로축은 시간(mm:ss, 세션 시작 기준)으로 오른쪽이
            현재입니다. 곡선 색은 음높이(저음 빨강 → 고음 보라), 곡선 끝 초록
            점이 현재 음(노트명 + 반음 대비 ¢)입니다. 라이브 중엔 최신을
            따라가고, 좌우 드래그로 과거를 볼 수 있습니다(● LIVE로 복귀). 하단
            Energy 막대는 입력 크기(rms)입니다.
          </InfoTip>
        </span>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSmooth((prev) => !prev)}
            aria-pressed={smooth}
            className={`rounded-md border px-3 py-1 text-xs font-medium transition motion-safe:active:scale-95 ${
              smooth
                ? 'border-primary/50 bg-primary/10 text-primary'
                : 'border-border/60 text-muted-foreground hover:bg-muted'
            }`}
          >
            median(3) 스무딩 {smooth ? 'ON' : 'OFF'}
          </button>
          <InfoTip label="median 스무딩 설명">
            한 옥타브 튀는 글리치를 줄이려고 최근 3개 값의 중앙값으로 곡선을
            다듬습니다. 끄면 원본 그대로, 켜면 부드럽지만 빠른 변화가 살짝
            늦습니다.
          </InfoTip>
        </span>
      </div>

      <div className="relative">
        <RealtimePitchGraph smooth={smooth} />
        {showEmpty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-md bg-background/70 text-center backdrop-blur-[1px]">
            <p className="text-sm font-medium text-foreground">
              시작을 누르면 여기에 음정이 표시됩니다
            </p>
            <p className="px-4 text-xs text-muted-foreground">
              {isNative
                ? '세션 시작 후 마이크 권한을 허용하면 실시간 곡선이 그려집니다. 값이 안 나오면 마이크 권한·입력 장치를 확인하세요.'
                : '데모 데이터 모드 — 시작하면 가짜 음정 곡선이 흐릅니다(실제 마이크 입력 아님).'}
            </p>
          </div>
        )}
        {showFrozenBadge && (
          <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-background/80 px-2 py-0.5 text-[11px] font-medium text-muted-foreground backdrop-blur-[1px]">
            정지됨 · 마지막 세션 (다시 시작하면 초기화)
          </span>
        )}
      </div>
    </div>
  );
};
